import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, PlusCircle, MoreHorizontal, BarChart3, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import MobileSlideOverMenu from './MobileSlideOverMenu';
import { useNavigation } from '../../context/NavigationContext';
import StarCoinsDisplay from '../../starCoinSystem/StarCoinsDisplay';
import GamePredictionButton from '../../gamePrediction/GamePredictionButton';
import { getBrazilianStatus } from '../../../utils/detectBrazil';
import { mobileTopBannerPtBR } from '../../mappings/mobileTopBanner.pt-br';

/**
 * Mobile-first top banner matching desktop functionality
 * Updated to remove March Madness focus and add modern features
 */
const MobileTopBanner = ({
  userId,
  userStats,
  responseMode,
  updateUIState,
  RESPONSE_MODES,
  handleNewChat,
  onSuggestionClick,
  onShowMobileStats,
  bets,
  setBets,
  // PickSlip functionality
  removeBet,
  updateBet,
  handleClearAll,
  handleManualAdd,
  handleExportToClipboard,
  showConfirmClear,
  setShowConfirmClear,
  showAddModal,
  setShowAddModal,
  newPickText,
  setNewPickText,
  deleteTargetId,
  setDeleteTargetId,
  showDeleteConfirm,
  setShowDeleteConfirm,
  handleConfirmDelete,
  // New props to match desktop
  tier,
  gameUsageState,
  runAutomatedGamePrediction,
  currentGameInfo = null
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState('main');
  const [showGameExplorerDot, setShowGameExplorerDot] = useState(false);
  const { inChatInterface } = useNavigation();
  const navigate = useNavigate();

  // Check if user is Brazilian
  const isBrazilian = getBrazilianStatus();
  
  // Translation function
  const t = (text) => {
    if (!isBrazilian) return text;
    return mobileTopBannerPtBR[text] || text;
  };

  // Check if user has clicked Game Explorer before (session-based)
  React.useEffect(() => {
    const hasClickedGameExplorer = sessionStorage.getItem('hasClickedGameExplorer');
    if (!hasClickedGameExplorer) {
      // Show the dot after a short delay for better UX
      const timer = setTimeout(() => {
        setShowGameExplorerDot(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  // Toggle main menu with optional specific view
  const toggleMainMenu = (view = 'main') => {
    // If opening Game Explorer, mark it as clicked for this session
    if (view === 'teamStats') {
      sessionStorage.setItem('hasClickedGameExplorer', 'true');
      setShowGameExplorerDot(false);
    }
    setMenuView(view);
    setIsMenuOpen(prev => !prev);
  };

  // Handle new chat
  const onNewChat = () => {
    handleNewChat();
    setIsMenuOpen(false);
  };

  return (
    <>
      <style jsx="true">{`
        /* Subtle pulse animation for the Pick Slip icon */
        @keyframes subtle-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.03); }
        }
        
        .subtle-pulse {
          animation: subtle-pulse 3s infinite ease-in-out;
        }
        
        /* Soft glow for icons */
        @keyframes soft-glow {
          0%, 100% { filter: drop-shadow(0 0 1px rgba(255,255,255,0.4)); }
          50% { filter: drop-shadow(0 0 3px rgba(255,255,255,0.6)); }
        }
        
        .soft-glow {
          animation: soft-glow 4s infinite ease-in-out;
        }
        
        /* Flying particles for Pick Slip */
        @keyframes fly-path-1 {
          0% { transform: translate(0px, 0px); opacity: 0; }
          10% { opacity: 0.8; }
          30% { transform: translate(-10px, -8px); }
          50% { transform: translate(5px, -12px); }
          70% { transform: translate(-8px, 5px); }
          90% { transform: translate(7px, 8px); opacity: 0.8; }
          100% { transform: translate(0px, 0px); opacity: 0; }
        }
        
        @keyframes fly-path-2 {
          0% { transform: translate(0px, 0px); opacity: 0; }
          10% { opacity: 0.8; }
          30% { transform: translate(10px, -7px); }
          50% { transform: translate(-7px, -10px); }
          70% { transform: translate(9px, 8px); }
          90% { transform: translate(-8px, 5px); opacity: 0.8; }
          100% { transform: translate(0px, 0px); opacity: 0; }
        }
        
        @keyframes fly-path-3 {
          0% { transform: translate(0px, 0px); opacity: 0; }
          10% { opacity: 0.8; }
          30% { transform: translate(-9px, 9px); }
          50% { transform: translate(-6px, -9px); }
          70% { transform: translate(10px, -5px); }
          90% { transform: translate(7px, 10px); opacity: 0.8; }
          100% { transform: translate(0px, 0px); opacity: 0; }
        }
        
        .fly-red-1 {
          animation: fly-path-1 10s infinite ease-in-out;
        }
        
        .fly-white-1 {
          animation: fly-path-2 11s infinite ease-in-out;
          animation-delay: -2s;
        }
        
        .fly-blue-1 {
          animation: fly-path-3 9s infinite ease-in-out;
          animation-delay: -4s;
        }
        
        /* Subtle float for badges */
        @keyframes subtle-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-1px); }
        }
        
        .subtle-float {
          animation: subtle-float 2s infinite ease-in-out;
        }
        
        /* Game banner animations */
        @keyframes banner-glow {
          0%, 100% { 
            box-shadow: 0 0 10px rgba(16, 185, 129, 0.3), 0 0 20px rgba(16, 185, 129, 0.1);
          }
          50% { 
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.5), 0 0 30px rgba(16, 185, 129, 0.2);
          }
        }
        
        .banner-glow {
          animation: banner-glow 2s infinite ease-in-out;
        }
        
        /* Mobile scroll fix for StarCoins modal */
        @media (max-width: 768px) {
          /* Ensure proper touch scrolling on mobile */
          :global(body) {
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }
          
          /* Fix for StarCoinsModal on mobile */
          :global(.fixed[style*="top: 57px"]) {
            top: 60px !important;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
          }
          
          /* Prevent scroll issues when modal is open */
          :global(.fixed[style*="top: 57px"][class*="z-40"]) {
            position: absolute !important;
            max-height: calc(100vh - 60px);
            overflow-y: auto;
          }
        }
      `}</style>

      {/* Mobile Header Bar - Terminal Glassmorphism Design */}
      <div className="w-full bg-black/90 backdrop-blur-lg border-b border-white/[0.05]">
        <div className="w-full px-3 py-2.5 flex items-center justify-between">
          {/* Left section: Menu button (when in chat) */}
          <div className="flex-none">
            {inChatInterface && (
              <button
                onClick={() => {
                  if (typeof window.toggleMobileMenu === 'function') {
                    window.toggleMobileMenu(); // Opens LEFT-side MobileNavigation
                  } else {
                    toggleMainMenu(); // Fallback to right-side menu
                  }
                }}
                className="p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] text-white/90 hover:text-white transition-all duration-200 active:scale-95 border border-white/[0.03]"
                aria-label={t("Menu")}
              >
                <Menu className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Center section: App title with proper spacing */}
          <div className="flex-1 flex justify-center px-4">
            <h1
              onClick={() => navigate('/home')}
              className="text-sm font-medium tracking-[0.25em] text-white/70 uppercase cursor-pointer hover:text-white/90 transition-all font-mono"
            >
              <span className="hidden min-[430px]:inline">{t("PickStar")}</span>
              <span className="inline min-[430px]:hidden">{t("PS")}</span>
            </h1>
          </div>

          {/* Right section: Primary actions with improved spacing */}
          <div className="flex-none flex items-center">
            {/* Group 1: Premium Features (Star Coins & Game Prediction) */}
            <div className="flex items-center mr-2 space-x-1.5">
              {/* Star Coins Display */}
              <StarCoinsDisplay userId={userId} isMobile={true} compact={true} />
              
              {/* Game Prediction Button */}
              <GamePredictionButton
                onClick={() => {
                  if (gameUsageState?.canUse) {
                    runAutomatedGamePrediction();
                  } else {
                    if (tier === 'basic') {
                      updateUIState({ showUpgradePrompt: true });
                    } else {
                      runAutomatedGamePrediction();
                    }
                  }
                }}
                remainingSpins={gameUsageState?.remaining}
                isBasicUser={tier === 'basic'}
                isMobile={true}
                resetTime={gameUsageState?.resetTime}
              />
            </div>

            {/* Group 2: Data Features (Game Explorer & Chat History) */}
            <div className="flex items-center mr-2 space-x-1.5">
              {/* Game Explorer Button - Terminal Style */}
              <button
                onClick={() => {
                  // Mark as clicked for this session
                  sessionStorage.setItem('hasClickedGameExplorer', 'true');
                  setShowGameExplorerDot(false);
                  // Navigate to Sports Hub
                  navigate('/sports-hub');
                }}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 active:scale-95"
                aria-label={t("Sports Hub")}
              >
                <BarChart3 className="w-4 h-4 text-white/50 hover:text-white/70" />
                {/* Notification dot */}
                {showGameExplorerDot && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 15
                    }}
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5"
                  >
                    <span className="absolute inset-0 bg-violet-400/40 rounded-full animate-ping" />
                    <span className="relative block w-2 h-2 bg-violet-400/60 rounded-full" />
                  </motion.div>
                )}
              </button>

              {/* Chat History Panel Button - Terminal Style */}
              <button
                onClick={onShowMobileStats}
                className="relative w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.02] backdrop-blur-sm border border-white/[0.05] hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200 active:scale-95 group"
                aria-label={t("Chat History")}
              >
                <Clock className="w-4 h-4 text-white/50 hover:text-white/70 transition-transform duration-200 group-hover:scale-105" />
                {/* Activity indicator - emerald glow */}
                <div className="absolute -top-0.5 -right-0.5">
                  <span className="absolute inset-0 w-1.5 h-1.5 bg-emerald-400/40 rounded-full animate-ping" />
                  <span className="relative block w-1.5 h-1.5 bg-emerald-400/80 rounded-full" />
                </div>
              </button>
            </div>

            {/* Group 3: Actions (New Chat & More) */}
            <div className="flex items-center space-x-1.5">
              {/* New Chat button - Terminal Style */}
              <button
                onClick={onNewChat}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.02] hover:bg-white/[0.05] text-white/40 hover:text-white/60 transition-all duration-200 active:scale-95 border border-white/[0.03]"
                aria-label={t("New Chat")}
              >
                <PlusCircle className="w-4 h-4" />
              </button>
              
              {/* More options button - Terminal Style */}
              <button
                onClick={() => {
                  setMenuView('main');
                  setIsMenuOpen(!isMenuOpen);
                }}
                className="w-9 h-9 flex items-center justify-center rounded-lg bg-white/[0.02] hover:bg-white/[0.05] text-white/30 hover:text-white/50 transition-all duration-200 active:scale-95 border border-white/[0.03]"
                aria-label={t("More options")}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Menu SlideOver - Rendered via Portal to escape stacking context */}
      {isMenuOpen && ReactDOM.createPortal(
        <AnimatePresence>
          {isMenuOpen && (
            <MobileSlideOverMenu
              userId={userId}
              userStats={userStats}
              responseMode={responseMode}
              updateUIState={updateUIState}
              RESPONSE_MODES={RESPONSE_MODES}
              handleNewChat={handleNewChat}
              onSuggestionClick={onSuggestionClick}
              onShowMobileStats={onShowMobileStats}
              onClose={() => setIsMenuOpen(false)}
              customView={menuView}
              bets={bets}
              setBets={setBets}
              // Pass PickSlip functionality
              removeBet={removeBet}
              updateBet={updateBet}
              handleClearAll={handleClearAll}
              handleManualAdd={handleManualAdd}
              handleExportToClipboard={handleExportToClipboard}
              showConfirmClear={showConfirmClear}
              setShowConfirmClear={setShowConfirmClear}
              showAddModal={showAddModal}
              setShowAddModal={setShowAddModal}
              newPickText={newPickText}
              setNewPickText={setNewPickText}
              deleteTargetId={deleteTargetId}
              setDeleteTargetId={setDeleteTargetId}
              showDeleteConfirm={showDeleteConfirm}
              setShowDeleteConfirm={setShowDeleteConfirm}
              handleConfirmDelete={handleConfirmDelete}
              // Add these new props for PickStar functionality
              tier={tier}
              gameUsageState={gameUsageState}
              runAutomatedGamePrediction={runAutomatedGamePrediction}
              currentGameInfo={currentGameInfo}
            />
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Beta Banner Modal - Removed from mobile */}
    </>
  );
};

export default MobileTopBanner;